import { getIsMobile } from "@songtradr/spa-common";
import { Col, Row } from "antd";
import { cloneDeep } from "lodash";
import React, { ReactElement, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import MobileDrawer from "src/components/mobile-drawer";
import useAuth from "src/auth/use-auth";
import FileUploader from "src/components/file-upload";
import { checkFilesCurrentlyUploading } from "src/components/file-upload/helpers";
import { IDeleteSelectedFile } from "src/components/file-upload/interfaces";
import {
  DocumentCategories,
  IProjectAttachment,
  IProjectRouteParams,
} from "src/pages/projects/project/interfaces";
import { AttachmentType } from "src/constants";
import CreateReportButton, {
  ReportFileName,
  ReportLabelName,
  ReportType,
} from "src/components/create-report-button";
import style from "../../../../styles";
import { IProjectUploadFile } from "../../../details/components/interfaces";
import { DocumentsTableColumns, DocumentsTableData } from "../../table-data";
import { FileDrawerTypes, generateDrawer } from "../mobile-drawers";

interface IProps {
  attachments: IProjectAttachment[];
  onIsLoadingReport: (isLoading: boolean) => void;
}

const License = ({ attachments, onIsLoadingReport }: IProps): ReactElement => {
  const { t } = useTranslation();
  const isMobile = getIsMobile();
  const { getAccessToken, organisationId } = useAuth();
  const [licenseFileList, setLicenseFileList] = useState<
    Array<IProjectUploadFile>
  >([]);
  const [accessToken, setAccessToken] = useState("");
  const { id: projectId } = useParams<IProjectRouteParams>();
  const [
    selectedFileToDelete,
    setSelectedFileToDelete,
  ] = useState<IDeleteSelectedFile>();
  const [showFileUploadDeleteModal, setShowFileUploadDeleteModal] = useState(
    false
  );
  const [isDrawerVisible, setDrawerVisible] = useState<boolean>(false);
  const [activeFile, setActiveFile] = useState<IProjectUploadFile>();
  const [activeDrawer, setActiveDrawer] = useState<FileDrawerTypes>(
    FileDrawerTypes.FileInfoDrawer
  );

  const handleSetLicenseFileList = useCallback(
    (files: IProjectUploadFile[]) => {
      setLicenseFileList(files);
    },
    []
  );
  const handleSetActiveDrawer = useCallback((drawerType: FileDrawerTypes) => {
    setActiveDrawer(drawerType);
  }, []);
  const handleSetSelectedFileToDelete = useCallback(
    (file: IDeleteSelectedFile | undefined) => {
      setSelectedFileToDelete(file);
    },
    []
  );
  const handleShowFileUploadDeleteModal = useCallback((show: boolean) => {
    setShowFileUploadDeleteModal(show);
  }, []);
  const handleSetActiveFile = useCallback(
    (file: IProjectUploadFile | undefined) => {
      setActiveFile(file);
    },
    []
  );
  const toggleDrawer = useCallback(() => {
    setDrawerVisible((state) => !state);
  }, []);

  useEffect(() => {
    if (!checkFilesCurrentlyUploading(licenseFileList)) {
      setLicenseFileList(cloneDeep(attachments));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments]);

  useEffect(() => {
    void (() => {
      const userAccessToken: string = getAccessToken();
      setAccessToken(userAccessToken);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div>
      <Row role="row" gutter={[8, 8]}>
        <Col span={24}>
          <div css={style.documentsUploadContainer}>
            <FileUploader
              attachmentType={AttachmentType.Documents}
              documentCategory={DocumentCategories.License}
              invalidFileSizeErrorMessage={t(
                "File size exceeds maximum limit of 2GB"
              )}
              existingFiles={licenseFileList}
              setFileList={handleSetLicenseFileList}
              tableData={DocumentsTableData({
                filesList: licenseFileList,
                accessToken,
                projectId,
                organisationId,
                setShowModal: handleShowFileUploadDeleteModal,
                setSelectedFileToDelete: handleSetSelectedFileToDelete,
                toggleDrawer,
                setActiveDrawer: handleSetActiveDrawer,
                setActiveFile: handleSetActiveFile,
              })}
              selectedFileToDelete={selectedFileToDelete}
              tableColumns={DocumentsTableColumns(isMobile, t)}
              showModal={showFileUploadDeleteModal}
              setShowModal={handleShowFileUploadDeleteModal}
              additionalContainerStyles={style.documentsFileUploader}
            />
          </div>
          <CreateReportButton
            dataTestId="download-license-request-report"
            onIsLoadingReport={onIsLoadingReport}
            ariaLabel={ReportLabelName.LicenseRequest}
            label={ReportLabelName.LicenseRequest}
            reportType={ReportType.LicenseRequest}
            reportFileName={ReportFileName.LicenseRequest}
          />
          <CreateReportButton
            dataTestId="download-master-license-agreement-report"
            onIsLoadingReport={onIsLoadingReport}
            ariaLabel={ReportLabelName.MasterLicenseAgreement}
            label={ReportLabelName.MasterLicenseAgreement}
            reportType={ReportType.MasterLicenseAgreement}
            reportFileName={ReportFileName.MasterLicenseAgreement}
          />
          <CreateReportButton
            dataTestId="download-publishing-license-agreement-report"
            onIsLoadingReport={onIsLoadingReport}
            ariaLabel={ReportLabelName.PublishingLicenseAgreement}
            label={ReportLabelName.PublishingLicenseAgreement}
            reportType={ReportType.PublishingLicenseAgreement}
            reportFileName={ReportFileName.PublishingLicenseAgreement}
          />
          <CreateReportButton
            dataTestId="download-master-single-song-assignment-report"
            onIsLoadingReport={onIsLoadingReport}
            ariaLabel={ReportLabelName.MasterSSA}
            label={ReportLabelName.MasterSSA}
            reportType={ReportType.MasterSSA}
            reportFileName={ReportFileName.MasterSSA}
          />
          <CreateReportButton
            dataTestId="download-publishing-song-assignment-report"
            onIsLoadingReport={onIsLoadingReport}
            ariaLabel={ReportLabelName.PublishingSSA}
            label={ReportLabelName.PublishingSSA}
            reportType={ReportType.PublishingSSA}
            reportFileName={ReportFileName.PublishingSSA}
          />
          <CreateReportButton
            dataTestId="download-schedule-a-report"
            onIsLoadingReport={onIsLoadingReport}
            ariaLabel={ReportLabelName.ScheduleAReport}
            label={ReportLabelName.ScheduleAReport}
            reportType={ReportType.ScheduleADocument}
            reportFileName={ReportFileName.ScheduleAReport}
          />
        </Col>
      </Row>
      {isMobile && (
        <MobileDrawer
          placement="bottom"
          onClose={toggleDrawer}
          open={isDrawerVisible}
          key="bottom"
          closable={false}
          data-testid="mobile-drawer-container"
          className="documents-drawer-container"
        >
          {generateDrawer(
            activeDrawer,
            handleSetActiveDrawer,
            toggleDrawer,
            accessToken,
            projectId,
            organisationId,
            handleSetLicenseFileList,
            licenseFileList,
            handleShowFileUploadDeleteModal,
            selectedFileToDelete,
            activeFile
          )}
        </MobileDrawer>
      )}
    </div>
  );
};

export default License;
